import { Component, createRef } from 'react';
import { filter } from 'rxjs/operators';
import { ExtensionInfoComponent } from '../../components/ui/extensions-info/extensions-info.component';
import { FeatureDetailsComponent } from '../../components/ui/feature-details/feature-details.component';
import FeaturesTilesComponent from '../../components/ui/features-tiles/features-tiles.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import RatingDisplayComponent from '../../components/ui/rating-display/rating-display.component';
import { RelatedLinksComponent } from '../../components/ui/related-links/related-links.component';
import { IOC_TYPES } from '../../context/ioc.types';
import { lazyInject } from '../../context/module';
import { FileUpload } from '../../models/file-upload';
import { Logger } from '../../models/logger';
import { DOMStorage } from '../../services/dom-storage.service';
import { EnvironmentService } from '../../services/environment.service';
import { LightHttp } from '../../services/light-http.service';
import { MotivesService } from '../../services/motives.service';
import { PageDataProviderService } from '../../services/page-data-provider.service';
import { SettingsService } from '../../services/settings.service';
import { UploadedFilesService } from '../../services/uploaded-files.service';
import { WindowService } from '../../services/window.service';
import { ExtensionSEOData, PageData, RouteProps, SeoFeature } from '../../types/route-types';
import { FromType, FromTypes, ToType, ToTypes } from '../../types/types';
import { BrowserUtils } from '../../ui-utils/browser-utils';
import { get } from '../../utils/lodash-min';
import { MotiveConfig, MotiveLocationTypes } from '../../utils/request.utils';
import { RouteUtils } from '../../utils/route.utils';
import { untilDestroyed } from '../../utils/until-destroyed';
import MotiveFrame from '../motive-frames/motive-frame.component';
import styles from './converter-page-base.component.module.scss';
import FileConvertComponent from './file-convert.component';


interface ConverterState {
    fileSelectBtnClicked: boolean;
}

interface ConverterProps extends RouteProps {

}


export default class ConverterPageBaseComponent extends Component<ConverterProps, ConverterState> {
    logger = Logger.getLogger('ConverterPageBaseComponent');

    el = createRef<HTMLDivElement>();
    @lazyInject(IOC_TYPES.EnvironmentService) env: EnvironmentService;
    @lazyInject(IOC_TYPES.LightHttp) lightHttp: LightHttp;
    @lazyInject(IOC_TYPES.DOMStorage) domStorage: DOMStorage;
    @lazyInject(IOC_TYPES.PageDataProviderService) pageDataProvider: PageDataProviderService;
    @lazyInject(IOC_TYPES.MotivesService) motivesService: MotivesService;


    @lazyInject(IOC_TYPES.UploadedFilesService) private uploadedFilesService: UploadedFilesService;
    @lazyInject(IOC_TYPES.MotivesService) public motiveService: MotivesService;
    @lazyInject(IOC_TYPES.SettingsService) private settingsService: SettingsService;
    @lazyInject(IOC_TYPES.WindowService) private windowService: WindowService;

    showTestComponents = false;

    get domainname() {
        return this.env?.domainname;
    }
    get sitename() {
        return this.env?.sitename;
    }

    get showRating() {
        return this.env?.sitename === this.env?.env.constants.xconvertId;
    }

    get showInfoGrid() {
        return this.env?.sitename === this.env?.env.constants.xconvertId;
    }

    // public sitename = 'xconvert';
    public title: string;
    public description: string;
    public features: Array<SeoFeature>;

    public showHTML = false;
    public isMobile: boolean;

    public feature;

    public markdownData;

    sidePanelFileUploads: FileUpload[];
    showSidePanelTriggered = false;
    canShowLivePreview = false;
    showLivePreview = false;

    effectiveMotives: MotiveConfig;
    extensionSeoDataList: ExtensionSEOData[] = [];

    featureDescriptionsDisabled = false;


    get internalAdsEnabled() {
        return this.effectiveMotives && this.effectiveMotives.internalMotivesEnabled;
    }
    get leftAdShown() {
        return this.effectiveMotives && this.effectiveMotives['convertPageLeft'] && !this.internalAdsEnabled;
    }
    get rightAdShown() {
        return this.effectiveMotives && this.effectiveMotives['convertPageRight'] && !this.internalAdsEnabled;
    }

    readonly state: Readonly<ConverterState> = { fileSelectBtnClicked: false };
    constructor(props: any) {
        super(props);
        this.effectiveMotives = this.motiveService.freeUserMotives;
        this.loadComponentData(this.props);
        this.updatePageData(props.pageData);
    }

    componentDidMount() {
        const componentMountedTime = Date.now();
        this.windowService.onFileSelectBtnClicked.pipe(
            filter(evt => evt.dispatchedAt.getTime() > componentMountedTime)
        ).subscribe({
            next: (data) => {
                if (!this.state.fileSelectBtnClicked) {
                    this.setState({
                        fileSelectBtnClicked: false,
                    });
                }
            }
        });
    }

    loadComponentData(props) {
        this.feature = RouteUtils.getPathFromProps(props);
        this.isMobile = BrowserUtils.isMobile();
        this.domStorage.setBaseComponentReadyAndFreezeContentIfNeeded();

        this.motivesService.onEffectiveMotiveSettingsChange
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (motives) => {
                    this.effectiveMotives = motives;
                    // Preact disable this until find a better way. For now its going though the route data
                    // this.changeKeyAndForceUpdate();
                },
                error: (error) => {
                    this.logger.error({ message: 'Motive settings change observer threw an error', error });
                    // this.changeKeyAndForceUpdate();
                },
            });


        this.settingsService.userSettings
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (userSettings) => {
                    this.showLivePreview = get(userSettings, 'featureFlags.showLivePreview', false);
                    this.featureDescriptionsDisabled = get(userSettings, 'featureDescriptionsDisabled', false);
                },
            });
    }

    shouldComponentUpdate(nextProp: ConverterProps, nextState: ConverterState) {
        const isDifferentURL = nextProp.url !== this.props.url;
        const isFileSelectBtnClickedStateChanged = nextState.fileSelectBtnClicked !== this.state.fileSelectBtnClicked;
        return isDifferentURL || isFileSelectBtnClickedStateChanged;
    }

    private updatePageData(value: PageData) {

        this.features = value.page_seo.feature;
        this.title = value.page_seo.title;
        this.description = value.page_seo.description && value.page_seo.description.replace(/\s+/g, ' ');
        this.uploadedFilesService.initializeForCompatibility(value.fromType, value.accept);
        this.uploadedFilesService.notyfyAll();

        this.updateCanShowLivePreview(value.fromType, value.toType);
    }

    componentWillUnmount() {
        // for untilDestroyed
    }

    updateCanShowLivePreview(fromType: FromType[], toType: ToType) {
        const notAllowedToTypes = [ToTypes.VIDEO]; // []; //
        const notAllowedFromTypes = [FromTypes.VIDEO, FromTypes.AV]; // []; //
        if (notAllowedToTypes.indexOf(toType) === -1 && !notAllowedFromTypes.find(notAllowedFromType => fromType.indexOf(notAllowedFromType) > -1)) {
            this.canShowLivePreview = true;
        } else {
            this.canShowLivePreview = false;
        }
    }

    public navigateToPage(pageHeaderLink: any) {
        if (pageHeaderLink.type === 'remote') {
            window.location.href = pageHeaderLink.url;
        }
        // TODO Preact fix
        // this.router.navigate([pageHeaderLink.url]);
    }

    getFIleConvertComponentHtml(props: ConverterProps) {
        return <FileConvertComponent {...props}>
        </FileConvertComponent>;
    }

    getHTML(props: ConverterProps) {
        const leftAdShown = this.leftAdShown;
        const rightAdShown = this.leftAdShown;
        const classes = {
            leftPane: '',
            contentPane: '',
            rightPane: '',
        }
        if (!rightAdShown && !leftAdShown) {
            classes.leftPane = 'col d-none';
            classes.contentPane = 'col-12 col-sm-12 col-md-12 col-lg-12';
            classes.rightPane = 'col d-none';
        } else if (rightAdShown && leftAdShown) {
            classes.leftPane = 'col d-none d-md-block col-md-3 col-lg-2';
            classes.contentPane = 'col-12 col-sm-12 col-md-9 col-lg-8';
            classes.rightPane = 'col d-none d-lg-block col-lg-2';
        } else if (!rightAdShown && leftAdShown) {
            classes.leftPane = 'col d-none d-md-block col-md-3 col-lg-2';
            classes.contentPane = 'col-12 col-sm-12 col-md-9 col-lg-10';
            classes.rightPane = 'col d-none';
        } else {
            classes.leftPane = 'col d-none';
            classes.contentPane = 'col-12 col-sm-12 col-md-9 col-lg-10';
            classes.rightPane = 'col d-none d-md-block col-md-3 col-lg-2';
        }
        return <div className="row nav-padding no-gutters">
            <div className={`${classes.leftPane}`}>
                {
                    leftAdShown && <div className={`gcontent-container ${styles['left-side-motive']}`}>
                        <MotiveComponent
                            data="6521450245" containerWidth="100%" containerHeight="600px"
                            location={MotiveLocationTypes.convertPageLeft}
                            adWidth="100%"
                            adHeight="600px" showAds={true} className='height_major'
                        >
                        </MotiveComponent>
                    </div>
                }
                {
                    this.motivesService?.internalAdsEnabled && <div className="gcontent-container">
                        Internal
                        {/* <internal-motive-display *ngIf="motivesService?.internalAdsEnabled" internalMotiveId="160"
                                containerWidth="100%" containerHeight="600px" adWidth="100%" adHeight="600px" [showAds]="true">
                            </internal-motive-display> */}
                    </div>
                }
            </div>

            {/* <div className={`upload-container ${classes.contentPane}`}>
                <div className="file-convert-parent">
                    <div className="row">
                        <div className="col-12 page-heading text-center">
                            <h1 className="title"><b>{this.title}</b></h1>
                            <p className="description secondary-text">{this.description}</p>
                        </div>
                    </div>
                    {
                        this.getFIleConvertComponentHtml(props)
                    }
                </div>

                <div>
                    {
                        <div className="gcontent-container">
                            <MotiveComponent
                                data='5605422308' containerWidth="100%" containerHeight="60px"
                                location={MotiveLocationTypes.convertPageBottom} adWidth="100%" adHeight="90px" showAds={true}
                                className='width_major'
                            >
                            </MotiveComponent>
                        </div>
                    }
                </div> 

                <div className="row text-center">
                    {
                        <div className="col-12 mt-3" >
                            <RelatedLinksComponent relatedLinks={this.props.relatedLinks}></RelatedLinksComponent>
                        </div>
                    }
                    {
                        <div className="col-12 mt-3" >
                            <FeatureDetailsComponent markdown={props.markdown}></FeatureDetailsComponent>
                        </div>
                    }
                    {
                        this.showRating && <div className="col-12 mt-3" >
                            <RatingDisplayComponent
                                rating={this.props.rating}
                                title={this.title}
                                feature={this.feature}
                                description={this.description}>
                            </RatingDisplayComponent>
                        </div>
                    }
                    {
                        this.showInfoGrid && !this.featureDescriptionsDisabled && <div className="col-12 mt-3">
                            {
                                this.features && <FeaturesTilesComponent features={this.features}></FeaturesTilesComponent>
                            }
                        </div>
                    }
                    {
                        <div className="col-12 mt-3" >
                            <ExtensionInfoComponent {...this.props}></ExtensionInfoComponent>
                        </div>
                    }
                </div>
            </div>
            {
                rightAdShown && <div key={'right-side-motive'} className={`${classes.rightPane}`}>
                    <div className={`gcontent-container right-side-motive ${styles['right-side-motive']}`}>
                        <MotiveComponent
                            data="4470001970" containerWidth="100%" containerHeight="600px"
                            location={MotiveLocationTypes.convertPageRight} adWidth="100%" adHeight="600px" showAds={true} className='height_major'
                        >
                        </MotiveComponent>
                    </div>
                    {
                        <div className="gcontent-container" >
                             <internal-motive-display  internalMotiveId="160"
                        containerWidth="100%" containerHeight="600px" adWidth="100%" adHeight="600px" [showAds]="true">
                    </internal-motive-display> 
                        </div>
                    }

                </div>
            } */}
        </div>
    }

    // shouldComponentUpdate(nextProps, t2) {
    //     if (this.props.path !== nextProps.path) {
    //         this.componentWillUnmount();
    //         this.loadComponentData(nextProps);
    //         this.updateKey();
    //     }
    //     return true;
    // }

    changeKeyAndForceUpdate() {
        this.updateKey();
        this.forceUpdate();
    }

    updateKey() {
        // this.setState({
        //     componentKey: StringUtils.getRandomString('key_'),
        // });
    }

    getSEOContent() {
        return <div className={`row ${styles['seo-container']}`}>
            {
                <div className="col-12 mt-3" >
                    <RelatedLinksComponent relatedLinks={this.props.relatedLinks}></RelatedLinksComponent>
                </div>
            }
            {
                <div className="col-12 mt-3" >
                    <FeatureDetailsComponent markdown={this.props.markdown}></FeatureDetailsComponent>
                </div>
            }
            {
                this.showRating && <div className="col-12 mt-3" >
                    <RatingDisplayComponent
                        rating={this.props.rating}
                        title={this.title}
                        feature={this.feature}
                        description={this.description}>
                    </RatingDisplayComponent>
                </div>
            }
            {
                this.showInfoGrid && !this.featureDescriptionsDisabled && <div className="col-12 mt-3">
                    {
                        this.features && <FeaturesTilesComponent features={this.features}></FeaturesTilesComponent>
                    }
                </div>
            }
            {
                <div className="col-12 mt-3" >
                    <ExtensionInfoComponent {...this.props}></ExtensionInfoComponent>
                </div>
            }
        </div>;
    }

    render() {
        return <MotiveFrame>
            <div className={`${styles['convert-page']} mx-1 ${this.state.fileSelectBtnClicked ? styles['file-selected'] : ''} pe-2 ps-2`}>
                <div>
                    {
                        this.getFIleConvertComponentHtml(this.props)
                    }
                </div>
                {
                    this.getSEOContent()
                }
            </div>
        </MotiveFrame>
    }
}